import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

interface ChatInputProps {
  onSendMessage: (messageText: string) => void;
  disabled?: boolean; // Optional prop to disable input
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled = false }) => {
  const [messageText, setMessageText] = useState<string>('');
  const theme = useTheme();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessageText(event.target.value);
  };

  const handleSend = () => {
    if (messageText.trim() === '') return;
    onSendMessage(messageText.trim());
    setMessageText('');
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    // Send on Enter press, but allow Shift+Enter for new line
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent default form submission or new line in TextField
      handleSend();
    }
  };

  return (
    <Box
        sx={{
            p: {xs: 1.5, sm: 2}, // Consistent padding
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900], // Slightly different bg for input area
            flexShrink: 0, // Prevent this Box from shrinking
        }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start"> {/* Align items to flex-start for multiline TextField */}
        <TextField
          fullWidth
          variant="outlined" // Standard variant
          size="small"
          placeholder="Send a message..." // Changed placeholder
          value={messageText}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={disabled}
          multiline
          maxRows={5} // Increased maxRows slightly
          sx={{
            flexGrow: 1, // Ensure TextField takes available space
            '& .MuiOutlinedInput-root': {
                borderRadius: '22px', // Pill shape
                // If you want the send button inside, more complex styling is needed here.
                // For now, send button is outside.
            },
            // Change border color on focus to primary
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
            },
          }}
        />
        <IconButton
            color="primary"
            onClick={handleSend}
            disabled={messageText.trim() === '' || disabled}
            aria-label="send message"
            sx={{
                // bgcolor: theme.palette.primary.main, // Use primary color from theme
                // color: theme.palette.primary.contrastText,
                // '&:hover': {
                //     bgcolor: theme.palette.primary.dark,
                // },
                // Ensure it aligns nicely with potentially multiline textfield
                mt: messageText.split('\n').length > 1 ? 0.5 : 0, // Adjust margin top if text field grows
            }}
        >
          <SendIcon />
        </IconButton>
      </Stack>
    </Box>
  );
};

export default ChatInput;
